import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function ContemporaryFinance54() {
  const contentRef = useRef(null);
  const titleText = "Fiat Money and The Decline of Civilization";
  const links = {
    prev: "/contemporaryfinance-53",
    index: "/index-finance",
    next: "/contemporaryfinance-55",
  };
  const questions = [
    {
      text: "1. What fundamental principle does fiat money attempt to bypass, according to the lesson?",
      options: [
        {
          value: "A",
          label: "A. The law of supply and demand",
        },
        {
          value: "B",
          label: "B. The natural progression of earning before spending",
        },
        {
          value: "C",
          label: "C. The requirement of government taxation",
        },
        {
          value: "D",
          label: "D. The limits of technological advancement",
        },
      ],
      correct: "B",
    },
    {
      text: "2. How did the shift from gold-backed money to fiat money influence human time preference?",
      options: [
        {
          value: "A",
          label: "A. It lowered time preference, encouraging long-term savings",
        },
        { value: "B", label: "B. It eliminated time preference altogether" },
        {
          value: "C",
          label:
            "C. It increased time preference, encouraging short-term consumption",
        },
        { value: "D", label: "D. It had no effect on time preference" },
      ],
      correct: "C",
    },
    {
      text: "3.  What architectural change is highlighted as evidence of fiat money’s impact on society?",
      options: [
        {
          value: "A",
          label: "A. Skyscrapers becoming taller due to better technology",
        },
        {
          value: "B",
          label:
            "B.  The decline in durable, long-lasting buildings compared to the gold standard era",
        },
        { value: "C", label: "C. The rise of eco-friendly homes" },
        { value: "D", label: "D. The expansion of urban areas" },
      ],
      correct: "C",
    },
    {
      text: "4. How does fiat money affect family and societal structures, as described in the lesson?",
      options: [
        {
          value: "A",
          label: "A. It strengthens family bonds through collective savings",
        },
        {
          value: "B",
          label: "B.  It reduces reliance on the state for social support",
        },
        {
          value: "C",
          label:
            "C. It often forces both spouses to work and shifts care of children and the elderly to the state",
        },
        {
          value: "D",
          label: "D. It eliminates generational differences in wealth",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={54}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 54</b>! In today’s lesson, we explore how fiat money
        does not simply serve as a neutral medium of exchange but actively
        shapes the way societies function and decay. Fiat is often viewed as a
        tool of convenience, enabling governments and banks to expand credit and
        meet immediate financial demands. Yet beneath this convenience lies a
        far-reaching distortion that alters human incentives, weakens
        institutions, and erodes the cultural and moral foundations of entire
        civilizations. By breaking the natural link between productivity and
        reward, fiat money allows individuals, businesses, and even nations to
        live beyond their means. This unearned access to wealth creates
        fragility, nurtures corruption, and undermines values like hard work,
        discipline, and responsibility. As we move through this lesson, we will
        see that fiat money influences far more than just prices and markets. It
        changes time preferences, making people live for today rather than build
        for tomorrow. It weakens the architecture of both our physical world and
        our social order, replacing durable structures and strong families with
        fragile systems sustained by debt and dependence. It distorts the
        environment by encouraging overconsumption of natural resources, and it
        expands the power of the state at the expense of individual freedom and
        community resilience.
        <br />
        <br />
        <b>Fiat Money as a Violation of Natural Law</b>
        <br />
        <br />
        Human civilization has always been guided by the basic truths of
        existence. The principle of sowing before reaping, of earning before
        spending, and of producing before being valued is a fundamental natural
        law. Throughout history, this natural law dictated how communities lived
        and prospered. A farmer could not hope to eat unless he had first
        cultivated his land. A craftsman could not trade unless he had put in
        effort and skill into producing something of value. Societies advanced
        by rewarding productivity and ensuring that individuals received
        recognition in proportion to their contribution. Fiat money, however,
        undermines this natural progression. Unlike real resources or productive
        labor, fiat currency is created without effort, sacrifice, or any
        underlying productive activity. It emerges from keystrokes on a central
        banker’s computer, detached from the tangible struggles of survival and
        creation. This mechanism distorts the moral fabric of society because it
        introduces wealth that has no basis in real productivity. Businesses
        that should naturally collapse due to inefficiency or mismanagement
        continue to survive merely because of their proximity to power,
        subsidies, or credit extensions offered through fiat channels. As a
        result, fiat nurtures political favoritism and punishes meritocracy. In
        an honest system of exchange, survival depends on performance and value
        creation. In a fiat system, survival often depends on political
        connections and artificial money flows. This creates a society where
        corruption thrives, entrepreneurship is stifled, and true merit is
        ignored, resulting in systemic rot that touches every institution. When
        natural law is circumvented, consequences inevitably follow, and fiat
        money stands as a glaring example of such a violation.
        <br />
        <br />
        <b>
          Fiat Money, Time Preference, and the Collapse of Future-Oriented
          Thinking
        </b>
        <br />
        <br />
        Money is not merely a neutral medium of exchange; it is a powerful
        technology that shapes human behavior. One of the most important
        economic concepts influenced by money is time preference. When a society
        has hard money, meaning money that is difficult to produce or inflate,
        people tend to have a low time preference. This means they are more
        inclined to think about the future, to save, to delay gratification, and
        to invest in endeavors that yield long-term benefits. In contrast, when
        money is easy to create, like fiat currency, societies develop high time
        preference behavior, where people focus primarily on immediate
        consumption and short-term survival. To understand this, one must
        consider how humans behaved before the invention of money. In a barter
        economy, saving was limited. A man who harvested barley could not simply
        store it indefinitely, because the grain would spoil. His incentive to
        save for future consumption was weak. However, the introduction of
        durable forms of money, such as gold and silver, transformed this
        equation. A gold coin did not spoil, decay, or lose its essence. It
        could be saved for decades and still exchanged for goods in the future.
        This durability incentivized saving, investment, and respect for future
        generations. Societies under the gold standard in the nineteenth century
        epitomized this phenomenon: people built businesses, institutions, and
        legacies with long-term horizons. When fiat money replaced gold, the
        reverse occurred. Since fiat could be created at will, its value eroded
        over time, making saving unattractive. People became uncertain about
        their financial future and shifted their focus to immediate
        gratification. This was not merely an economic change but also a
        cultural one. Rising crime rates, collapsing family values, and the
        breakdown of social norms are all downstream effects of a society where
        people no longer believe in the future. Fiat money pushed humanity
        backward, to a state reminiscent of primitive times, where survival
        instincts overrode trust in long-term planning. The collapse of
        future-oriented thinking is one of the greatest damages caused by fiat
        money, and its corrosive impact is visible in every aspect of modern
        life.
        <br />
        <br />
        <b>The Architecture of Civilization under Hard and Soft Money</b>
        <br />
        <br />
        One of the most visible domains where the impact of fiat money can be
        observed is architecture. Buildings are among the most enduring symbols
        of human civilization, and they reveal the time preference of the
        societies that constructed them. Under the gold standard in the
        nineteenth century, when money was hard, people invested in structures
        that were meant to last centuries. Palaces, libraries, bridges, and
        family homes were constructed with stone, wood, and craftsmanship
        designed to endure. These buildings represented not only wealth but also
        faith in the future. A family that invested in a house knew that
        generations to come would live under the same roof. A nation that built
        monumental public structures did so with the belief that its
        civilization would endure long enough to justify the expense. The advent
        of fiat money in the twentieth century shifted priorities. With money
        losing its purchasing power and high time preference dominating social
        behavior, construction became focused on short-term cost minimization
        rather than long-term durability. Glass towers, cheap housing projects,
        and disposable infrastructure became the norm. While technological
        advancements made construction faster and less expensive, they also led
        to fragile designs that required constant maintenance and renovation.
        The supposed savings of the fiat era often proved illusory, because the
        long-term costs of upkeep and replacement outweighed the upfront
        reductions. This phenomenon extended beyond material concerns into the
        very spirit of human creativity. Whereas the architecture of the gold
        standard age reflected a sense of permanence and reverence, fiat-era
        architecture reflects transience, disposability, and an obsession with
        appearances rather than substance. Walking through the streets of many
        modern cities, one sees hollow glass towers rather than enduring works
        of art. The high time preference induced by fiat money is literally
        etched into the skylines of our civilization, serving as a silent
        testimony to our short-sightedness and loss of vision.
        <br />
        <br />
        <b>Debt, Consumption, and the Exploitation of Natural Resources</b>
        <br />
        <br />
        Another devastating consequence of fiat money lies in its relentless
        encouragement of debt and overconsumption. Because fiat can be created
        without limit, borrowing becomes not only easy but also incentivized.
        Governments borrow to cover deficits, businesses borrow to expand
        recklessly, and individuals borrow to finance lifestyles beyond their
        means. The result is a world drowning in debt, where true savings are
        rare and most wealth is an illusion sustained by continuous credit
        creation. As fiat money loses its purchasing power, people become
        desperate to preserve their wealth. Cash in the bank depreciates, so
        individuals are pushed into speculative investments, often
        miscalculating risks in their rush to escape inflation. This dynamic
        creates bubbles that eventually burst, destroying livelihoods and entire
        economies. The impact of fiat extends beyond finance into the realm of
        nature. A society obsessed with short-term consumption discounts the
        value of the future. Rivers are polluted, forests are cleared, soil is
        overexploited, and beaches are commercialized without consideration for
        sustainability. Fiat money, by eroding the incentive to save and plan
        long-term, creates a civilization that cannibalizes its own environment.
        The depletion of natural resources is not merely the result of
        industrialization, but also of a financial system that discourages
        prudence. Hard money civilizations treated nature with more reverence,
        not out of altruism but because they recognized the importance of
        conserving resources for future use. Fiat societies, by contrast,
        exploit everything in sight, prioritizing immediate profit over
        long-term stewardship. Thus, fiat money’s destructive impetus is both
        financial and ecological, leaving humanity poorer not only in wealth but
        also in natural abundance. The constant expansion of credit, combined
        with the erosion of money’s value, leads to a civilization that burns
        tomorrow to enjoy today, leaving behind a trail of economic fragility
        and ecological ruin.
        <br />
        <br />
        <b>Fiat Money and the Breakdown of Family and Community</b>
        <br />
        <br />
        Perhaps the most tragic impact of fiat money is its effect on the
        family, the most fundamental unit of human society. In hard money
        civilizations, families were strong, cohesive, and oriented toward
        long-term stability. Parents invested in their children with the
        expectation that these children would carry forward their legacy.
        Sacrificing present consumption for the sake of future security was
        natural, because money preserved its value and future planning was
        rational. In contrast, fiat money undermines the very foundations of
        family life. As the value of money declines, it becomes increasingly
        difficult for a single income to support a household. Families that once
        thrived on one breadwinner are now forced into dual-income arrangements,
        leaving less time for nurturing relationships or raising children.
        Children are often raised by institutions or strangers, while the
        elderly are placed under state care, eroding the bonds of
        intergenerational responsibility. This weakening of the family is not
        accidental; it is the logical outcome of a system that destroys
        incentives to save and invest in one’s kin. Beyond finances, fiat money
        fosters cultural decay. When people lose faith in the future, they also
        lose faith in building lasting relationships. Marriage rates decline,
        divorce rates rise, and communities fracture. Instead of families and
        neighborhoods providing support, individuals increasingly turn to the
        state, which becomes both caretaker and master. Fiat money thus replaces
        the family with bureaucracy, reducing human beings to isolated economic
        units dependent on central authority. The consequences extend even
        further into education, religion, and community trust. Parents who once
        viewed children as blessings for the future may instead see them as
        financial liabilities in a world where money continually loses value.
        Faith institutions, which were once heavily funded by generational
        savings, struggle to survive as donations decline in an inflationary
        economy. Local communities that thrived on trust and shared
        responsibility become hollow shells where interactions are transactional
        rather than relational. Fiat money, by eroding the family and community,
        does not merely weaken the economy; it dismantles the very foundations
        of civilization. A society where people no longer invest in each other
        is a society destined to collapse, and fiat money accelerates that
        collapse with every printed note and every keystroke at a central bank.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today’s lesson highlighted how fiat money disrupts the natural order of
        productivity and reward, creating a society where unearned wealth fuels
        corruption, fragility, and moral decay. By lowering time preference,
        hard money once encouraged people to build for the future, but fiat
        raises time preference, making people focus on immediate gratification
        at the cost of long-term stability. This distortion is visible in
        fragile architecture, reckless use of natural resources, rising personal
        and national debt, and the erosion of family structures as economic
        pressures force both spouses to work and weaken intergenerational bonds.
        Fiat not only alters economic choices but also undermines cultural and
        social institutions, shifting responsibility from families and
        communities to the state. The result is a civilization that becomes less
        resilient, more dependent, and more vulnerable to collapse.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In the next lesson, we move beyond finance and into one of the most
        intimate areas of human life: food. We will examine how fiat money,
        inflation, and monetary intervention reshape agriculture, influence what
        farmers grow, and determine what ultimately reaches our plates. From the
        end of the gold standard to the rise of industrial farming and processed
        foods, we will see how monetary distortion quietly alters diets,
        nutrition, and health, revealing that fiat money is not just an economic
        system, but a force that reaches into kitchens, bodies, and daily
        survival itself.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <p className="content">
        1. What fundamental principle does fiat money attempt to bypass,
        according to the lesson?
        <ul>
          <li>A. The law of supply and demand</li>
          <li>B. The natural progression of earning before spending</li>
          <li>C. The requirement of government taxation</li>
          <li>D. The limits of technological advancement</li>
        </ul>
      </p>
      <p className="content">
        2. How did the shift from gold-backed money to fiat money influence
        human time preference?
        <ul>
          <li>A. It lowered time preference, encouraging long-term savings</li>
          <li>B. It eliminated time preference altogether</li>
          <li>
            C. It increased time preference, encouraging short-term consumption
          </li>
          <li>D. It had no effect on time preference</li>
        </ul>
      </p>
      <p className="content">
        3. What architectural change is highlighted as evidence of fiat money’s
        impact on society?
        <ul>
          <li>A. Skyscrapers becoming taller due to better technology</li>
          <li>
            B. The decline in durable, long-lasting buildings compared to the
            gold standard era
          </li>
          <li>C. The rise of eco-friendly homes</li>
          <li>D. The expansion of urban areas</li>
        </ul>
      </p>
      <p className="content">
        4. How does fiat money affect family and societal structures, as
        described in the lesson?
        <ul>
          <li>A. It strengthens family bonds through collective savings</li>
          <li>B. It reduces reliance on the state for social support</li>
          <li>
            C. It often forces both spouses to work and shifts care of children
            and the elderly to the state
          </li>
          <li>D. It eliminates generational differences in wealth</li>
        </ul>
      </p>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default ContemporaryFinance54;
